import type { ExplorerPane } from 'types/explorer'

import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import {
  ALBUM_VIEW,
  ARTIST_VIEW,
  DEFAULT_VIEW,
  PLAYLIST_VIEW,
  TRACK_VIEW,
} from '@disco/constants/ViewTypes'

/**
 * Generates explorer pane parameters based on search type and sorting preferences.
 *
 * Maps search types to their corresponding view types and applies the appropriate
 * sort parameters. Each content type (track, playlist, album, artist) has its
 * own view configuration and sort parameter mapping.
 *
 * @param params - Configuration object
 * @param params.type - Type of content being searched ('track', 'playlist', 'album', 'artist', 'channel', or false)
 * @param params.sort - Sort field to apply
 * @param params.sortDirection - Sort direction ('asc' or 'desc')
 * @returns Partial explorer pane configuration object
 *
 * @example
 * ```typescript
 * // For track search
 * const trackParams = getPaneParamsBasedOnSearchType({
 *   type: 'track',
 *   sort: 'title',
 *   sortDirection: 'asc'
 * })
 * // Returns: { view: 'track', trackSortParam: 'title', trackSortDirection: 'asc' }
 *
 * // For playlist search
 * const playlistParams = getPaneParamsBasedOnSearchType({
 *   type: 'playlist',
 *   sort: 'created',
 *   sortDirection: 'desc'
 * })
 * // Returns: { view: 'playlist', playlistSortParam: 'created', playlistSortDirection: 'desc' }
 *
 * // For no specific type
 * const defaultParams = getPaneParamsBasedOnSearchType({
 *   type: false,
 *   sort: 'title',
 *   sortDirection: 'asc'
 * })
 * // Returns: { view: 'default' }
 * ```
 */
export const getPaneParamsBasedOnSearchType = ({
  type,
  sort,
  sortDirection,
}: {
  type: false | 'track' | 'playlist' | 'album' | 'artist' | 'channel'
  sort: SORT
  sortDirection: SORT_DIRECTION
}): Partial<ExplorerPane> => {
  if (!type)
    return {
      view: DEFAULT_VIEW,
    }
  if (type === 'playlist')
    return {
      view: PLAYLIST_VIEW,
      playlistSortParam: sort,
      playlistSortDirection: sortDirection,
    }
  if (type === 'album')
    return {
      view: ALBUM_VIEW,
      playlistSortParam: sort,
      playlistSortDirection: sortDirection,
    }
  if (type === 'artist')
    return {
      view: ARTIST_VIEW,
      artistSortParam: sort,
      artistSortDirection: sortDirection,
    }
  if (type === 'track')
    return {
      view: TRACK_VIEW,
      trackSortParam: sort,
      trackSortDirection: sortDirection,
    }

  return {}
}
